import React, { useCallback, useEffect, useRef, useState } from "react";
import jwt_decode from "jwt-decode";
import { toast } from "react-toastify";
import sendServerLogging from "../../utils/sendServerLogging";
import userJSON from "../../utils/userJSON";
import useFocusTrap from "../../hooks/useFocusTrap";
import { getSessionToken } from "../../lib/sessionToken";
import { logInfo } from "../../lib/sentryLogging";

const AddPatientModal = ({
  showModal,
  setShowModal,
  setPatientsChange,
  pickedDate,
  specialty,
  org,
}) => {
  const [description, setDescription] = useState("");
  const [patientData, setPatientData] = useState({});
  const [importType, setImportType] = useState("");
  const [appointmentID, getAppointmentID] = useState("");
  const [patientType, setPatientType] = useState("");
  const [surgery, setSurgery] = useState(false);
  const [MRN, setMRN] = useState("");
  const [highlightMRN, setHighlightMRN] = useState(false);
  const [highlightPatientName, setHighlightPatientName] = useState(false);
  const [requiredMessage, setRequiredMessage] = useState("");
  const inputRef = useRef(null);
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);

  const clearRequiredMessage = (nextDescription, nextMRN) => {
    if (!requiredMessage) {
      return;
    }
    const hasDescription = nextDescription.trim().length > 0;
    const hasMRN = nextMRN.trim().length > 0;

    if (
      requiredMessage === "Patient Name is required to add a patient." &&
      hasDescription
    ) {
      setRequiredMessage("");
      return;
    }

    if (
      requiredMessage === "MRN/DOB/SSN is required to add a patient." &&
      hasMRN
    ) {
      setRequiredMessage("");
      return;
    }

    if (
      requiredMessage ===
      "Patient Name and MRN/DOB/SSN are required to add a patient." &&
      hasDescription &&
      hasMRN
    ) {
      setRequiredMessage("");
    }
  };

  const handleDescriptionChange = (value) => {
    setDescription(value);
    if (highlightPatientName && value.trim()) {
      setHighlightPatientName(false);
    }
    clearRequiredMessage(value, MRN);
  };

  const handleMRNChange = (value) => {
    setMRN(value);
    if (highlightMRN && value.trim()) {
      setHighlightMRN(false);
    }
    clearRequiredMessage(description, value);
  };

  const handleClose = useCallback(() => {
    setShowModal(false);
    setDescription("");
    setMRN("");
    setHighlightMRN(false);
    setHighlightPatientName(false);
    setRequiredMessage("");
    setSurgery(false);
    setPatientData({});
    setImportType("");
    getAppointmentID("");
    setPatientType("");
  }, [setShowModal]);

  useEffect(() => {
    const handleCloseEvent = () => {
      handleClose();
    };

    window.addEventListener("close-add-patient-modal", handleCloseEvent);
    return () => {
      window.removeEventListener("close-add-patient-modal", handleCloseEvent);
    };
  }, [handleClose]);

  useFocusTrap(showModal, dialogRef, null, handleClose);

  const changeStatusToReview = async (patient_id) => {
    try {
      const status = "Review";
      const body = { status };

      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");
      myHeaders.append("token", getSessionToken());

      await fetch(
        `${process.env.REACT_APP_API_URL}/notes/status/${patient_id}`,
        {
          method: "PUT",
          headers: myHeaders,
          body: JSON.stringify(body),
        }
      );
    } catch (err) {
      console.error(err.message);
    }
  };

  const onSubmitForm = async (e) => {
    e.preventDefault();

    const trimmedDescription = description.trim();
    const trimmedMRN = MRN.trim();
    if (!trimmedDescription || !trimmedMRN) {
      if (!trimmedDescription) {
        setHighlightPatientName(true);
        inputRef.current?.focus();
      }
      if (!trimmedMRN) {
        setHighlightMRN(true);
      }

      if (!trimmedDescription && trimmedMRN) {
        setRequiredMessage("Patient Name is required to add a patient.");
      } else if (trimmedDescription && !trimmedMRN) {
        setRequiredMessage("MRN/DOB/SSN is required to add a patient.");
      } else if (!trimmedDescription && !trimmedMRN) {
        setRequiredMessage(
          "Patient Name and MRN/DOB/SSN are required to add a patient."
        );
      }
      return;
    }

    const decoded_token = jwt_decode(getSessionToken());
    const user_id = decoded_token["user"]["id"];
    try {
      const isoDate = new Date().toISOString().split("T")[0];
      const patientDataPayload =
        patientData && patientData.start
          ? patientData
          : { start: `${isoDate}T14:00:00-05:00` };
      const myHeaders = new Headers();

      myHeaders.append("Content-Type", "application/json");
      myHeaders.append("token", getSessionToken());
      const body = {
        description,
        pickedDate,
        MRN,
        patientData: patientDataPayload,
        importType,
        appointmentID,
        patientType,
        surgery,
      };
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/dashboard/todos`,
          {
            method: "POST",
            headers: myHeaders,
            body: JSON.stringify(body),
          }
        );
        const data = await response.json();
        // get user_id from token
        if (user_id === "f2d1b78f-68ae-47c2-9216-05f3b9cc04b8") {
          await changeStatusToReview(data.patient_id);
        }

        //server logging
        sendServerLogging({
          body: {
            ...userJSON(localStorage.getItem("userName")),
            event: "ADD_PATIENT",
            eventData: {
              mrn: MRN,
            },
            patientId: patientData.patient_id,
          },
        });
        if (response.ok) {
          logInfo("Patient manually created", "patient.created", {
            patient_id: data.patient_id,
            source: "manual",
            patient_type: patientType,
            import_type: importType,
          });
        }

        setPatientsChange(true);
        handleClose();
        toast.success("Patient added successfully!");
      } catch (error) {
        console.error("Error posting to /dashboard/todos:", error);
        toast.error("Failed to add patient. Please try again.");
      }
    } catch (err) {
      console.error(err.message);
      toast.error("An error occurred. Please try again.");
    }
  };

  const handleAddTestPatient = async ({ forTutorial = false } = {}) => {
    // The tutorial creates "QQQVAINTERFACE,IFC" (no "TWO"): the backend gives it
    // a pre-populated Smart Chart and the recorder routes it through the staged
    // note pipeline. Manual adds use the "TWO" variant.
    const testName = forTutorial ? "QQQVAINTERFACE,IFC" : "QQQVAINTERFACE,IFC TWO";
    const testMrn = "1061312409V524429";
    const testAppointmentId = "1061312409V524429";
    const testApptTime = "14:00:00";
    const now = new Date();
    const isoDate = now.toISOString().split("T")[0];

    try {
      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");
      myHeaders.append("token", getSessionToken());

      const body = {
        description: testName,
        pickedDate,
        MRN: testMrn,
        patientData: {
          start: `${isoDate}T${testApptTime}-05:00`,
        },
        importType: "Demo Insert",
        appointmentID: testAppointmentId,
        patientType: "test_patient",
        surgery,
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/dashboard/todos`,
        {
          method: "POST",
          headers: myHeaders,
          body: JSON.stringify(body),
        }
      );
      const data = await response.json();
      const decoded_token = jwt_decode(getSessionToken());
      const user_id = decoded_token["user"]["id"];
      if (user_id === "f2d1b78f-68ae-47c2-9216-05f3b9cc04b8") {
        await changeStatusToReview(data.patient_id);
      }

      sendServerLogging({
        body: {
          ...userJSON(localStorage.getItem("userName")),
          event: "ADD_PATIENT",
          eventData: {
            mrn: testMrn,
          },
          patientId: patientData.patient_id,
        },
      });
      if (response.ok) {
        logInfo("Patient manually created", "patient.created", {
          patient_id: data.patient_id,
          source: forTutorial ? "tutorial" : "test_patient",
          patient_type: "test_patient",
          import_type: "Demo Insert",
        });
      }

      setPatientsChange(true);
      handleClose();
      toast.success("Test patient added successfully.");
    } catch (error) {
      console.error("Error posting test patient to /dashboard/todos:", error);
      toast.error("Failed to add test patient. Please try again.");
    }
  };

  // The onboarding tutorial creates the demo patient for the user by firing
  // this event on its "Add a Veteran" step.
  useEffect(() => {
    const handleTutorialAddTestPatient = () => {
      handleAddTestPatient({ forTutorial: true });
    };
    window.addEventListener("tutorial-add-test-patient", handleTutorialAddTestPatient);
    return () => {
      window.removeEventListener("tutorial-add-test-patient", handleTutorialAddTestPatient);
    };
  }, [handleAddTestPatient]);

  if (!showModal) return null;
  return (
    <div className="fixed bg-black/40 backdrop-blur-sm inset-0 flex items-center justify-center z-50">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add_patient_modal_title"
        className="relative flex flex-col bg-white p-10 rounded-2xl shadow-2xl w-[90vw] max-w-[480px]"
      >
        <button
          ref={closeButtonRef}
          id="add_patient_modal_close"
          type="button"
          aria-label="Close Add Veteran modal"
          onClick={handleClose}
          className="absolute top-5 right-5 w-9 h-9 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
        >
          <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="text-center mb-8">
          <h2
            id="add_patient_modal_title"
            className="text-[26px] text-[#1a1f36] font-extrabold tracking-tight"
          >
            Add Veteran
          </h2>
        </div>

        <div className="mb-6 rounded-xl border border-indigo-100 bg-indigo-50/60 px-4 py-3 text-sm text-indigo-900">
          Enter the Veteran's full name and a unique identifier (MRN, DOB, or SSN).
          Then click "Add Veteran" to create the Veteran and start the note for the
          selected date.
        </div>

        <form className="w-full" onSubmit={onSubmitForm}>
          <div className="mb-4">
            <label
              htmlFor="add_patient_name"
              className="block text-sm font-medium text-gray-700 mb-1.5"
            >
              Veteran Name
            </label>
            <div className="relative">
              <input
                id="add_patient_name"
                type="text"
                aria-required="true"
                aria-invalid={highlightPatientName || undefined}
                aria-describedby={highlightPatientName ? "add_patient_error" : undefined}
                placeholder="Enter veteran name"
                className="w-full py-3.5 pl-11 pr-4 text-sm border border-gray-200 rounded-xl bg-gray-50 text-gray-800 placeholder-gray-600 transition-all focus:outline-none focus:border-indigo-300 focus:bg-white focus:ring-[3px] focus:ring-indigo-100"
                value={description}
                ref={inputRef}
                onChange={(e) => handleDescriptionChange(e.target.value)}
              />
              <div className="absolute inset-y-0 left-0 flex items-center pl-4">
                <svg
                  aria-hidden="true"
                  className="w-5 h-5 text-gray-400"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M16 12h4m-2 2v-4M4 18v-1a3 3 0 0 1 3-3h4a3 3 0 0 1 3 3v1c0 .6-.4 1-1 1H5a1 1 0 0 1-1-1Zm8-10a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                  />
                </svg>
              </div>
            </div>
          </div>

          <div className="mb-4">
            <label
              htmlFor="add_patient_identifier"
              className="block text-sm font-medium text-gray-700 mb-1.5"
            >
              MRN/DOB/SSN
            </label>
            <div className="relative">
              <input
                id="add_patient_identifier"
                type="text"
                aria-required="true"
                aria-invalid={highlightMRN || undefined}
                aria-describedby={highlightMRN ? "add_patient_error" : undefined}
                placeholder="Enter MRN, DOB, or SSN"
                className="w-full py-3.5 pl-11 pr-4 text-sm border border-gray-200 rounded-xl bg-gray-50 text-gray-800 placeholder-gray-600 transition-all focus:outline-none focus:border-indigo-300 focus:bg-white focus:ring-[3px] focus:ring-indigo-100"
                value={MRN}
                onChange={(e) => handleMRNChange(e.target.value)}
              />
              <div className="absolute inset-y-0 left-0 flex items-center pl-4">
                <svg
                  aria-hidden="true"
                  className="w-5 h-5 text-gray-400"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 4h3c.6 0 1 .4 1 1v15c0 .6-.4 1-1 1H6a1 1 0 0 1-1-1V5c0-.6.4-1 1-1h3m0 3h6m-6 7 2 2 4-4m-5-9v4h4V3h-4Z"
                  />
                </svg>
              </div>
            </div>
          </div>

          {specialty === "Plastic Surgery" && (
            <div className="mb-4">
              <label
                htmlFor="template"
                className="block text-sm font-medium text-gray-700 mb-1.5"
              >
                Template
              </label>
              <select
                id="template"
                name="template"
                className="w-full py-3.5 pl-4 pr-11 text-sm border border-gray-200 rounded-xl bg-gray-50 text-gray-800 appearance-none cursor-pointer transition-all focus:outline-none focus:border-indigo-300 focus:bg-white focus:ring-[3px] focus:ring-indigo-100 bg-[url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%236b7280%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpath%20d%3D%22M6%209l6%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-no-repeat bg-[right_14px_center]"
                value={surgery ? "op-note" : ""}
                onChange={(e) => {
                  setSurgery(e.target.value === "op-note");
                }}
              >
                <option value="">Visit Note Template</option>
                <option value="op-note">Op-Note</option>
              </select>
            </div>
          )}

          {description.trim() && MRN.trim() && !requiredMessage && (
            <div className="flex items-center gap-2.5 p-3.5 bg-indigo-50 rounded-xl mb-4">
              <svg aria-hidden="true" className="w-5 h-5 text-indigo-600 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm text-indigo-600">Please ensure that all information is accurate</span>
            </div>
          )}

          <div role="alert">
          {requiredMessage && (
            <div className="flex items-center gap-2.5 p-3.5 bg-red-50 rounded-xl mb-4">
              <svg aria-hidden="true" className="w-5 h-5 text-red-700 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span id="add_patient_error" className="text-sm text-red-700 font-medium">
                {requiredMessage}
              </span>
            </div>
          )}
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 py-3.5 px-6 rounded-xl text-sm font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors kx-focus"
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`flex-1 py-3.5 px-6 rounded-xl text-sm font-semibold text-white transition-all kx-focus ${!description.trim() || !MRN.trim()
                  ? "bg-gray-300 cursor-not-allowed"
                  : "bg-accent2 hover:bg-purple2 shadow-lg shadow-accent2/35 hover:shadow-xl hover:shadow-accent2/40 hover:-translate-y-0.5"
                }`}
              disabled={!description.trim() || !MRN.trim()}
            >
              Add Veteran
            </button>
          </div>
        </form>

        <div className="flex items-center my-4">
          <div className="flex-1 border-b border-gray-200"></div>
          <span className="px-4 text-xs text-gray-600 uppercase tracking-wide">add test patient</span>
          <div className="flex-1 border-b border-gray-200"></div>
        </div>

        <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-y border-r border-emerald-200 border-l-4 border-l-emerald-500 rounded-r-xl p-4 pl-5 flex flex-col gap-2.5">
          <div className="flex items-center gap-2.5">
            <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <span className="text-sm font-extrabold text-emerald-800">Just testing?</span>
          </div>
          <p className="text-xs text-emerald-800 leading-relaxed">
            Add a test patient to practice recording and uploading a note to the EHR.
          </p>
          <p className="text-[10px] text-emerald-800 italic">
            Test patients will appear as "QQQVAINTERFACE, IFC TWO".
          </p>
          <button
            type="button"
            id="add_test_patient_button"
            onClick={() => handleAddTestPatient()}
            className="self-start mt-1 px-5 py-2.5 text-xs font-bold rounded-lg bg-emerald-700 text-white shadow-md shadow-emerald-700/30 hover:bg-emerald-800 hover:shadow-lg hover:shadow-emerald-800/40 hover:-translate-y-0.5 transition-all kx-focus"
          >
            Add Test Patient
          </button>
        </div>




      </div>
    </div>
  )
}

export default AddPatientModal;